import { toNumber } from "lodash";
import { LoopStep } from "../usePlayer/loopSteps";
import Duration from "../constants/Duration";

export interface Params {
  melodyShorthand: string;
}

export interface Return {
  melodySteps: LoopStep[];
  error: string | null;
}

export const maxNotes = 16;

export const errors = {
  noMelody: "No melody notes can be found in the shorthand provided.",
  invalidCharacter:
    "Invalid character found in shorthand. Only numbers, dashes, and spaces are allowed.",
  invalidDuration:
    "Only quarter, half, dotted half, and full notes are allowed. The only available options are: (1 1- 1-- 1---).",
  invalidNote:
    "Only scale degrees 1-8 are allowed. The only available options are: (1 2 3 4 5 6 7 8).",
  needSpaceBetweenNumbersAndDashes:
    "There needs to be a space between numbers and dashes. (Instead of 1--1 use 1-- 1, etc).",
  dashMustFollowNumber:
    "A dash must follow a number. (Make sure all - have a number before them and not a space).",
  maxNotesExceeded: `The maximum number of notes (${maxNotes}) has been exceeded.`,
};

interface ParsedQuarterNote {
  degree: number;
  duration: Duration;
}

export default function shortHandToLoopSteps({
  melodyShorthand,
}: Params): Return {
  if (!checkCharsAreValid(melodyShorthand)) {
    return {
      melodySteps: [],
      error: errors.invalidCharacter,
    };
  }

  let error: string | null = null;
  const parsedQuarterNotes: ParsedQuarterNote[] = [];
  const melodySplitNotes = melodyShorthand
    .split(/ +/)
    .map((str) => str.trim())
    .filter((str) => str.length > 0);

  for (let i = 0; i < melodySplitNotes.length; i++) {
    const parsed = parseQuarterNote({ str: melodySplitNotes[i] });

    if (isError(parsed)) {
      error = parsed.error;

      return {
        melodySteps: [],
        error,
      };
    }

    parsedQuarterNotes.push(parsed);
  }

  if (parsedQuarterNotes.length > maxNotes) {
    return {
      error: errors.maxNotesExceeded,
      melodySteps: [],
    };
  }

  if (parsedQuarterNotes.length === 0) {
    return {
      error: errors.noMelody,
      melodySteps: [],
    };
  }

  const melodySteps: LoopStep[] = [];

  for (let i = 0; i < parsedQuarterNotes.length; i++) {
    const { degree, duration } = parsedQuarterNotes[i];

    const loopStep: LoopStep = {
      chord: undefined,
      lastStep: false,
      melody: {
        degreesToPlay: [degree],
        duration,
      },
      metronome: true,
      type: "degree",
    };

    melodySteps.push(loopStep);

    // Generate rest steps needed after a note longer then a quarter note is added
    const restStepsNeeded = getRestStepCountByDuration(duration);

    if (isError(restStepsNeeded)) {
      return {
        melodySteps: [],
        error: restStepsNeeded.error,
      };
    }

    for (let i = 0; i < restStepsNeeded; i++) {
      melodySteps.push({
        lastStep: false,
        metronome: true,
        type: "rest",
      });
    }
  }

  // Set the last step to true
  melodySteps[melodySteps.length - 1].lastStep = true;

  if (error) {
    return {
      melodySteps: [],
      error,
    };
  }

  return {
    melodySteps,
    error: null,
  };
}

function parseQuarterNote({
  str,
}: {
  str: string;
}): ParsedQuarterNote | { error: string } {
  let remaingStr = str.trim();

  // does not have any spaces in the middle, only contains digits or dashes
  const onlyHasValidChars = checkCharsAreValid(remaingStr);

  if (!onlyHasValidChars) {
    return { error: errors.invalidCharacter };
  }

  // Check if the note is negative and remove the negative sign
  const isNegative = remaingStr[0] === "-";

  if (isNegative) {
    // remove the negative sign

    remaingStr = remaingStr.slice(1);
  }

  if (remaingStr[0] === "-" || remaingStr.length === 0) {
    return { error: errors.dashMustFollowNumber };
  }

  // Get all chars that are numbers
  let digits = "";

  while (remaingStr.length > 0 && /^[0-9]$/.test(remaingStr[0])) {
    digits += remaingStr[0];
    remaingStr = remaingStr.slice(1);
  }

  // get count of remaining dashes
  let dashes = 0;

  while (remaingStr.length > 0 && remaingStr[0] === "-") {
    dashes += 1;
    remaingStr = remaingStr.slice(1);
  }

  if (remaingStr.length > 0) {
    return { error: errors.needSpaceBetweenNumbersAndDashes };
  }

  let number = toNumber(digits);

  if (!isFinite(number)) {
    return { error: errors.invalidCharacter };
  }

  number = isNegative ? -number : number;

  if (number < 1 || number > 8) {
    return { error: errors.invalidNote };
  }

  const duration = getDurationFromDashCount(dashes);

  if (typeof duration !== "string") {
    return { error: duration.error };
  }

  return {
    degree: number,
    duration,
  };
}

function getDurationFromDashCount(dashes: number) {
  if (dashes === 0) {
    return Duration.quarterNote;
  } else if (dashes === 1) {
    return Duration.halfNote;
  } else if (dashes === 2) {
    return Duration.dottedHalfNote;
  } else if (dashes === 3) {
    return Duration.fullNote;
  }

  return { error: errors.invalidDuration };
}

function getRestStepCountByDuration(
  duration: Duration
): number | { error: string } {
  if (duration === Duration.quarterNote) {
    return 0;
  }

  if (duration === Duration.halfNote) {
    return 1;
  }

  if (duration === Duration.dottedHalfNote) {
    return 2;
  }

  if (duration === Duration.fullNote) {
    return 3;
  }

  return { error: errors.invalidDuration };
}

function isError(
  parsed: ParsedQuarterNote | { error: string } | number
): parsed is { error: string } {
  if (typeof parsed === "number") {
    return false;
  }

  return (parsed as { error: string })?.error !== undefined;
}

function checkCharsAreValid(str: string) {
  return /^[0-9-\s]*$/.test(str);
}
